/**
 * Zwei Ringpuffer für die Meldung: Konsolenfehler und fehlgeschlagene Requests.
 *
 * „Speichern geht nicht" wird erst brauchbar, wenn daneben steht, welcher Aufruf
 * mit welchem Status gescheitert ist. Die Request-Id kommt vom Server
 * (`X-Request-Id`) und führt direkt in dessen Protokoll.
 *
 * Gespeichert wird **nur** Methode, Pfad, Status und Request-Id — keine
 * Antwortkörper, keine Formularinhalte. Was im Puffer liegt, bekommt der Melder
 * vor dem Absenden zu sehen.
 *
 * Nur Beta-Testphase (docs/testing-tickets-plan.md); fällt mit dem Modul weg.
 */

export interface KonsolenEintrag {
  ts: string;
  level: 'error' | 'warn';
  message: string;
}

export interface RequestEintrag {
  ts: string;
  method: string;
  path: string;
  status: number;
  requestId?: string;
}

const MAX_KONSOLE = 20;
const MAX_REQUESTS = 10;
/** Eine einzelne Zeile bleibt kurz — ein Stacktrace sprengt sonst jedes Ticket. */
const MAX_ZEICHEN = 500;

const konsole: KonsolenEintrag[] = [];
const requests: RequestEintrag[] = [];

const schiebe = <T>(puffer: T[], eintrag: T, grenze: number): void => {
  puffer.push(eintrag);
  if (puffer.length > grenze) puffer.splice(0, puffer.length - grenze);
};

const jetzt = (): string => new Date().toISOString();

/** Pfad ohne Query: dort stehen gelegentlich Suchbegriffe, die uns nichts angehen. */
const pfadOhneQuery = (url: string): string => {
  const ohneQuery = url.split('?')[0] ?? url;
  return ohneQuery.slice(0, 200);
};

export function merkeFehlgeschlagenenRequest(eintrag: {
  method: string;
  url: string;
  status: number;
  requestId?: string | null;
}): void {
  schiebe(
    requests,
    {
      ts: jetzt(),
      method: (eintrag.method || 'GET').toUpperCase(),
      path: pfadOhneQuery(eintrag.url),
      status: eintrag.status,
      ...(eintrag.requestId ? { requestId: eintrag.requestId.slice(0, 64) } : {}),
    },
    MAX_REQUESTS,
  );
}

export function merkeKonsolenfehler(level: 'error' | 'warn', args: unknown[]): void {
  const text = args
    .map((wert) => {
      if (typeof wert === 'string') return wert;
      if (wert instanceof Error) return `${wert.name}: ${wert.message}`;
      try {
        return JSON.stringify(wert);
      } catch {
        return String(wert);
      }
    })
    .join(' ')
    .slice(0, MAX_ZEICHEN);
  if (!text.trim()) return;
  schiebe(konsole, { ts: jetzt(), level, message: text }, MAX_KONSOLE);
}

export const konsolenPuffer = (): KonsolenEintrag[] => [...konsole];
export const requestPuffer = (): RequestEintrag[] => [...requests];

let installiert = false;

/**
 * Hängt sich an `console.error`/`console.warn`.
 *
 * Die ursprüngliche Ausgabe läuft unverändert weiter — der Puffer hört nur mit.
 * Einmal pro Sitzung; ein zweiter Aufruf tut nichts.
 */
export function installiereKonsolenMitschnitt(): void {
  if (installiert || typeof console === 'undefined') return;
  installiert = true;
  (['error', 'warn'] as const).forEach((level) => {
    const original = console[level]?.bind(console);
    if (!original) return;
    console[level] = (...args: unknown[]) => {
      try {
        merkeKonsolenfehler(level, args);
      } catch {
        // Der Mitschnitt darf die Ausgabe nie verhindern.
      }
      original(...args);
    };
  });
}
